import { useRef } from 'react';
import useOnLoad from '../../../../hooks/queries/useOnLoad';
import useStatus from '../../../../hooks/stateful/useStatus';
import useBatchPaAgreementSwitch from './useBatchPaAgreementSwitch';
import useBatchDownloadSelection from './useBatchDownloadSelection';
import acquisitionTypes from '../../../../support/acquisitionTypes';
import { getIsMultiPaBulkDownloadActive } from '../../../multiPaBulkDownload';
import {
  withCanonicalAgreementOrder,
  withPreferredPaFirst,
  getIsSingleAgreement,
  getCanEditAgreementSelection,
} from '../agreementDerivations';
import {
  PREMIUM_ACCESS_TYPE,
  type BatchAgreement,
  type PremiumAccessAgreementSummary,
  type BatchAgreementsData,
} from '../types';

const useBatchAgreements = (): BatchAgreementsData | null => {
  const { response } = useOnLoad() as {
    response?: {
      config?: {
        batchAgreements?: BatchAgreement[];
        userHasMultiplePaAgreements?: boolean;
        availablePremiumAccessAgreements?: PremiumAccessAgreementSummary[];
      };
    };
  };
  const {
    batchAgreements,
    userHasMultiplePaAgreements,
    availablePremiumAccessAgreements,
  } = response?.config ?? {};
  const { selectAgreementOption } = useBatchDownloadSelection();
  const { status } = useStatus();
  const {
    switchPremiumAccessAgreement,
    isSwitchingPremiumAccessAgreement,
    switchingToPremiumAccessId,
    switchPremiumAccessAgreementError,
  } = useBatchPaAgreementSwitch();
  // Snapshotted once so the picker's order stays put as the user switches
  // agreements — only the initial default should determine position;
  // currentPaAgreementId below still tracks the live selection for the
  // radio highlight.
  const defaultPaIdRef = useRef<number | undefined>(undefined);

  if (!batchAgreements) return null;

  const orderedBatchAgreements = withCanonicalAgreementOrder(batchAgreements);

  const currentBatchPaAgreement = orderedBatchAgreements.find(
    (agreement) => agreement.productType === PREMIUM_ACCESS_TYPE
  );

  if (defaultPaIdRef.current === undefined) {
    defaultPaIdRef.current = currentBatchPaAgreement?.id;
  }

  const showPaPicker = (availablePremiumAccessAgreements?.length ?? 0) > 1;

  const orderedAvailablePremiumAccessAgreements =
    availablePremiumAccessAgreements
      ? withPreferredPaFirst(
          availablePremiumAccessAgreements,
          defaultPaIdRef.current
        )
      : availablePremiumAccessAgreements;

  const isSingleAgreement = getIsSingleAgreement({
    batchAgreements: orderedBatchAgreements,
    showPaPicker,
    availablePaCount: availablePremiumAccessAgreements?.length ?? 0,
    hasCurrentPaAgreement: currentBatchPaAgreement?.id !== undefined,
  });

  const canEditAgreementSelection = getCanEditAgreementSelection(status);

  const onSelectPaAgreement = (nextId: number) => {
    if (isSwitchingPremiumAccessAgreement) return;
    if (nextId === currentBatchPaAgreement?.id) return;

    switchPremiumAccessAgreement(String(nextId), {
      onSuccess: (freshResult) => {
        const freshOptions =
          freshResult?.configs?.[acquisitionTypes.BATCH_DOWNLOAD]?.options;
        selectAgreementOption(freshOptions);
      },
    });
  };

  return {
    batchAgreements: orderedBatchAgreements,
    availablePremiumAccessAgreements: orderedAvailablePremiumAccessAgreements,
    userHasMultiplePaAgreements: !!userHasMultiplePaAgreements,
    showPaPicker,
    currentPaAgreementId: currentBatchPaAgreement?.id,
    isSwitchingPremiumAccessAgreement,
    switchingToPremiumAccessId,
    switchPremiumAccessAgreementError,
    onSelectPaAgreement,
    isSingleAgreement,
    canEditAgreementSelection,
    multiPaBulkDownloadActive: getIsMultiPaBulkDownloadActive(
      availablePremiumAccessAgreements
    ),
  };
};

export default useBatchAgreements;
